import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { screenSizes } from "../helpers/screenSizes";

import { LandingStyle } from "../styles/landing.style";
import { StyledButton } from "../styles/button.style";
import { StyledOpenButton } from "../styles/button.style";
import useMediaQuery from "../hooks/useMediaQuery";
import Stars from "./Stars";

const Home = () => {
  // images
  const comet =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627793/mad_wallet/images/home/mdw-metero.png";
  const planet1 =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627799/mad_wallet/images/home/mdw-world.png";
  const astroid3 =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627794/mad_wallet/images/home/meteor3.png";
  const astroid1 =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627791/mad_wallet/images/home/meteor1.png";
  const astroid2 =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627791/mad_wallet/images/home/meteor2.png";
  const spaceship =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627791/mad_wallet/images/home/mdw-spaceship.png";
  const introFrame =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627805/mad_wallet/images/home/mdw__intro.png";
  const spaceman =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627797/mad_wallet/images/home/mdw-astraunut.png";
  const planet2 =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1652291863/mad_wallet/images/home/world_02.psdxfv-Recovered.psdhv.pngfg.png";
  const background =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1652492655/mad_wallet/images/home/space_back_ground.jpg";
  const button =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627792/mad_wallet/images/buttons/mdw-btn.png";
  const buttonOpen =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627791/mad_wallet/images/buttons/mdw_btn-open.png";
  const buttonClicked =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1649627791/mad_wallet/images/buttons/mdw__btn-clicked.png";
  const introFrameMb =
    "https://res.cloudinary.com/dahddvlgy/image/upload/v1651182997/mad_wallet/images/mobile/Panels/why-madwallet-panel.png";

  const landing = useRef(null);

  const [screens, setScreens] = useState([]);
  const [buttonClick, setButtonClick] = useState(false);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    setScreens(screenSizes);

    // Hiding commet
    let hideCommet = gsap.timeline({
      scrollTrigger: {
        trigger: landing.current,
        scroller: "body",
        start: "top top",
        end: `+=30`,
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });
    hideCommet.to(".metero", { opacity: 0 });

    // Default animation for landing page
    gsap
      .timeline()
      .to(".spaceship", {
        x: 80,
        y: -120,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "none",
      })
      .fromTo(
        ".metero",
        { x: 50, y: -50, scale: 1, yoyo: true },
        { x: -1000, y: 300, duration: 4, scale: 0, repeat: -1, repeatDelay: 5 },
        "<"
      )
      .to(
        ".meteros",
        {
          rotation: 360,
          transformOrigin: "center",
          repeat: -1,
          ease: "none",
          duration: 20,
        },
        "<"
      )
      .to(
        ".astraunut",
        {
          translateX: 20,
          translateY: 20,
          duration: 4,
          repeat: -1,
          yoyo: true,
          ease: "linear",
        },
        "<"
      )
      .to(
        ".spaceman-wrapper",
        {
          rotate: -10,
          duration: 4,
          repeat: -1,
          yoyo: true,
          ease: "linear",
        },
        "<"
      )
      .to(
        ".metero1",
        {
          rotation: 360,
          transformOrigin: "center",
          repeat: -1,
          duration: 12,
          ease: "none",
        },
        "<"
      )
      .to(
        ".metero3",
        {
          rotation: 360,
          transformOrigin: "center",
          repeat: -1,
          duration: 8,
          ease: "none",
        },
        "<"
      )
      .to(
        ".world-two",
        {
          rotate: 360,
          yoyo: true,
          transformOrigin: "center center",
          repeat: -1,
          duration: 40,
          ease: "none",
        },
        "<"
      );
  }, []);

  useEffect(() => {
    let landingAnimations = gsap.timeline({
      scrollTrigger: {
        trigger: landing.current,
        scroller: "body",
        start: "top top",
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    let playLandingAnimations = (options) => {
      landingAnimations
        .to("#landing", { backgroundSize: "100% 100%" })
        .to(
          ".mdw-world",
          {
            scale: 0.25,
            x: options.worldOne.xPosition,
            y: options.worldOne.yPosition,
          },
          "<"
        )
        .to(".spaceship", { scale: 0.2 }, "<")
        .to(
          ".metero1",
          {
            scale: 0.2,
            x: options.meteroOne.xPosition,
            y: options.meteroOne.yPosition,
          },
          "<"
        )
        .to(
          ".metero2",
          {
            scale: 0.2,
            x: options.meteroTwo.xPosition,
            y: options.meteroTwo.yPosition,
          },
          "<"
        )
        .to(
          ".metero3",
          {
            scale: 0.2,
            x: options.meteroThree.xPosition,
            y: options.meteroThree.yPosition,
          },
          "<"
        )
        .to(".content h1", { opacity: 0, duration: 0.2 }, "<")
        .to(".content p", { opacity: 0, duration: 0.2 }, "<")
        .to(".interactive", { opacity: 0, duration: 0.2 }, "<")
        .to(".space-world", { left: "50%" }, "<")
        .to(".world-two", { scale: 1, left: options.worldTwo.left }, "<")
        .to(
          ".spaceman-wrapper",
          { top: options.spacemanWrap.top, left: options.spacemanWrap.left },
          "<"
        )
        .to(".astraunut", { scale: 0.4 }, "<")
        .fromTo(
          ".intro-message",
          { y: 400, opacity: 0 },
          { y: -40, opacity: 1 },
          ">-0.1"
        )
        .to(
          ".content",
          {
            background:
              "linear-gradient(to top, rgba(0, 0, 0, 1), transparent)",
          },
          "<"
        );
    };

    const mediaQuery = (query) => window.matchMedia(query).matches;

    const mediaWidths = [];

    // choose from list of sizes
    // the smallest, since array of sizes is shown at any given viewport

    screens.forEach((screen) => {
      if (mediaQuery(screen.size)) {
        mediaWidths.push(screen.width);
      }
    });

    let { size } = {
      ...screens.filter(
        (screen) => screen.width === Math.min(...mediaWidths)
      )[0],
    };

    if (mediaQuery(size)) {
      let { options } = {
        ...screenSizes.filter((screen) => screen.size === size)[0],
      };
      ScrollTrigger.matchMedia({
        [size]: function () {
          playLandingAnimations(options);
        },
      });
    }
  }, [screens]);

  const matches = useMediaQuery("(max-width: 768px)");

  return (
    <LandingStyle id="landing" bg={background} ref={landing}>
      <Stars />
      <div className="metero">
        <img src={comet} alt="falling metero" />
      </div>
      <div className="mdw-world">
        <img src={planet1} alt="world from space" />
      </div>
      <div className="meteros metero3">
        <img src={astroid3} alt="Third moving astroid" />
      </div>
      <div className="meteros metero1">
        <img src={astroid1} alt="moving astroid" />
      </div>
      <div className="meteros metero2">
        <img src={astroid2} alt="Second moving astroid" />
      </div>
      <div className="spaceship">
        <img src={spaceship} alt="alien spaceship" />
      </div>

      <div className="content">
        <h1 className="title">
          Welcome to <span>Madwallet</span>
        </h1>
        <p>One decentralized wallet across multiple metaverses</p>
        <div className="interactive">
          <StyledButton
            bg={buttonClick ? buttonClicked : button}
            onClick={() => setButtonClick(true)}
          >
            <span>Download Extension</span>
          </StyledButton>
          <StyledOpenButton bg={buttonOpen}>
            <div>
              <span>
                <i className="fa fa-play" aria-hidden="true"></i>
              </span>
              <span>video</span>
            </div>
          </StyledOpenButton>
        </div>
      </div>
      <div className="intro-message">
        <img
          src={matches ? introFrameMb : introFrame}
          alt="introducing madwallet"
        />
        <p className="intro-text">
          MAD (Metaverse Advanced Development) Wallet gives users access to
          multiple metaverse implementations across multiple blockchains.
        </p>
      </div>
      <div className="space-world">
        <div className="spaceman-wrapper">
          <div className="astraunut">
            <img src={spaceman} alt="spaceman in space" />
          </div>
        </div>
        <div className="world-two">
          <img src={planet2} alt="into the world" />
        </div>
      </div>
    </LandingStyle>
  );
};

export default Home;
